import {
  Checkbox,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import {
  type AuthenticationFormValues,
  AuthenticationType
} from 'components/status-checks/authentication-form-utils'
import { ObservabilityToolAwsSecretsManagerAuthenticationOption } from 'components/status-checks/form/authentication/ObservabilityToolAwsSecretsManagerAuthenticationOption'
import { ObservabilityToolPrivateNetworkIntegration } from 'components/status-checks/form/authentication/ObservabilityToolPrivateNetworkIntegration'
import { ObservabilityToolPrometheusAuthenticationUserPasswordDetails } from 'components/status-checks/form/authentication/prometheus/ObservabilityToolPrometheusAuthenticationUserPasswordDetails'
import { ObservabilityToolPrometheusAuthenticationUserPasswordOption } from 'components/status-checks/form/authentication/prometheus/ObservabilityToolPrometheusAuthenticationUserPasswordOption'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useFeatureFlags } from 'hooks/use-feature-flags'

const fieldsToWatch = [
  'authentication.password',
  'authentication.username',
  'authenticationType',
  'isPrivateNetworkIntegration'
] as const

/**
 * Renders a `username` and `password` field required for creating a new Prometheus Authentication.
 * These fields are combined as `username:password` and are base64 encoded to form a Basic Auth token.
 */
export const ObservabilityToolPrometheusAuthenticationDetails = () => {
  const { isEdit } = useObservabilityToolAuthenticationContext()
  const { control, setValue, trigger } =
    useFormContext<AuthenticationFormValues>()

  const { credentialRetrieval: isCredentialRetrievalEnabled } =
    useFeatureFlags()

  const [password, username, authenticationType, isPrivateNetworkIntegration] =
    useWatch({
      control,
      name: fieldsToWatch
    })

  /*
   * Given a valid username and password value, create an encoded string based on the
   * `username:password` combination to attach to the `headers` field as a Basic Auth token.
   */
  useEffect(() => {
    // re-evaluate the authentication fields if neither value is present. This should clear lingering errors.
    if (!username && !password) {
      trigger('authentication')
      return
    }

    // Encode nothing if either username or password are falsy, and reset the headers field state to an invalid value.
    if (!username || !password) {
      setValue('headers', [], { shouldDirty: true })

      return
    }

    const encodedApiKey = btoa(`${username}:${password}`)

    setValue(
      'headers',
      [{ key: 'Authentication', value: `Basic ${encodedApiKey}` }],
      { shouldDirty: true }
    )
  }, [username, password, setValue, trigger])

  return (
    <Stack spacing={2}>
      <FormControl>
        <Controller
          control={control}
          name="authenticationUrl"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              data-testid="observability-tool-authentication-api-base-url-input"
              disabled={isEdit}
              error={Boolean(error)}
              helperText={error?.message}
              label="Prometheus URL"
            />
          )}
        />
      </FormControl>
      <ObservabilityToolPrivateNetworkIntegration />
      <Controller
        control={control}
        name="authenticationType"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={authenticationType !== undefined}
                data-testid="observability-tool-authentication-use-authentication-checkbox"
                sx={{
                  paddingLeft: 0
                }}
                onChange={(e) => {
                  setValue('headers', [], { shouldDirty: true })
                  setValue('secret', '', { shouldDirty: true })
                  field.onChange(
                    e.target.checked
                      ? AuthenticationType.UsernamePassword
                      : undefined
                  )
                }}
              />
            }
            label="Use authentication (optional)"
          />
        )}
      />
      {authenticationType && (
        <>
          <Typography variant="h6">Authentication</Typography>
          {isPrivateNetworkIntegration && isCredentialRetrievalEnabled ? (
            <>
              <Typography>
                Which method would you like to authenticate your observability
                tool with?
              </Typography>
              <FormControl>
                <Controller
                  control={control}
                  name="authenticationType"
                  render={({ field }) => {
                    return (
                      <RadioGroup
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value as AuthenticationType
                          field.onChange(value)

                          trigger(['headers', 'secret'])
                        }}
                      >
                        <ObservabilityToolPrometheusAuthenticationUserPasswordOption />
                        <ObservabilityToolAwsSecretsManagerAuthenticationOption />
                      </RadioGroup>
                    )
                  }}
                />
              </FormControl>
            </>
          ) : (
            <ObservabilityToolPrometheusAuthenticationUserPasswordDetails />
          )}
        </>
      )}
    </Stack>
  )
}
